import { kebabCase } from 'lodash-es'
import { useAppStore } from 'src/stores/appStore.js'
/**
 * Formatting helper functions
 */

export function number(val, dp = 0) {
    return Intl.NumberFormat(undefined, { maximumFractionDigits: dp, minimumFractionDigits: dp })
        .format(val || 0)
}

export function numberWithOptionalDecimals(val, dp = 2) {
    return Intl.NumberFormat(undefined, { maximumFractionDigits: dp, minimumFractionDigits: 0 })
        .format(val || 0)
}

export function currency(val, currencyId = null, mantissa = 2) {
    let symbol = currencyId
        ? useAppStore().lookupsById.currency[currencyId].symbol ?? '$'
        : '$'

    // eslint-disable-next-line no-irregular-whitespace
    let formatted = `${symbol} ${number(Math.abs(val), mantissa)}`
    if (val < 0) formatted = `-${formatted}`
    return formatted
}

export function shortNumber(val) {
    if (val > 1000000) return `${number(val / 1000000, 1)}m`
    if (val > 1000) return `${number(val / 1000, 1)}k`
    return number(val)
}

export function percent(val, dp = 0) {
    return `${number(val * 100, dp)}%`
}

export function bytes(bytes, decimals = 1) {
    if (!bytes) return '0 Bytes'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export function date(val, format = 'YYYY-MM-DD') {
    if (!val) return ''

    if (!(val instanceof dayjs)) val = dayjs(val)
    return val.format(format)
}

export function longDate(val) {
    return date(val, 'LL')
}

export function longDateTime(val) {
    return date(toLocalTime(val), 'LLL')
}

export function time(val, format = 'LT') {
    return date(toLocalTime(val), format)
}

export function sqlDate(val) {
    return date(val, 'YYYY-MM-DD')
}

export function sqlTime(val) {
    return date(val, 'HH:mm:ss')
}

export function sqlTimestamp(val = null) {
    return date(val || dayjs().utc(), 'YYYY-MM-DD HH:mm:ss')
}

export function shortTimeAgo(val) {
    if (!(val instanceof dayjs)) {
        val = dayjs(val)
    }
    let days = dayjs().diff(val, 'days')
    return days > 365
        ? `${Math.round(days / 365)}y` // in # years
        : `${days}d` // in # days
}

export function relativeDate(val) {
    if (!(val instanceof dayjs)) {
        val = dayjs(val)
    }

    return val.from(dayjs())
}

export function timestamp(val) {
    return toLocalTime(val).fromNow()
}

/**
 * Just a relative timestamp without a time component
 */
export function datestamp(val) {
    if (!(val instanceof dayjs)) val = dayjs(val)

    let days = val.diff(dayjs().startOf('day'), 'days')
    switch (days) {
        case 0: return 'today'
        case 1: return 'tomorrow'
        case -1: return 'yesterday'
        default:
            return dayjs.duration(days, 'days').humanize(true)
    }
}

export function mailTo(email, subject) {
    let mailto = `mailto:${email}`
    if (subject) {
        mailto += `?subject=${subject}`
    }
    return mailto
}

export function slug(str) {
    if (!str) return ''
    return kebabCase(str.toLowerCase())
}

export function plural(singluarForm, pluralForm, count) {
    return count == 1 ? singluarForm : pluralForm
}

export function isNegative(num) {
    return Math.sign(num) === -1
}

// Convert from local time to UTC
export function toUTC(val) {
    return dayjs(val).utc()
}

// Convert from UTC to local time
export function toLocalTime(val) {
    return dayjs.utc(val).local()
}

/**
 * Round the value to the nearest multiple
 */
export function roundToNearest(val, multiple) {
    return Math.round(val / multiple) * multiple
}

export function currentPeriod() {
    return dayjs().format('YYYYMM')
}

export function humaniseSeconds(seconds) {
    return seconds > 3600 // 1 hour
        ? dayjs.duration(seconds, 'seconds').format('H[h] m[m] s[s]')
        : dayjs.duration(seconds, 'seconds').format('m[m] s[s]')
}
